import { useState } from 'react';
import type { ItemSpec } from '../../../engine';
import {
  curioSlots,
  isBolted,
  itemSellValue,
  mantelSlots,
  mantelUsed,
  onLoan,
  type NightRun,
  type Profile,
} from '../../../campaign';
import { t } from '../../../i18n';
import { itemName, itemText } from '../../../app/describe';
import { Modal } from '../Modal';
import {
  ItemCard,
  ItemSheet,
  isBenchCurio,
  showCurio,
  showFixture,
  showMantelItem,
  type SheetAction,
} from './Item';
import { Mantel } from './Mantel';
import { RateCard } from './RateCard';

/**
 * What a run owns: the Mantel (in its order, which matters), Curios, Appraisals, Fixtures and
 * Favors, and the roster with its Carvings.
 */

export const pawnLabel = (p: Profile, run: NightRun, id: string): string => {
  const standIn = run.roster.find((r) => r.id === id)?.standIn;
  return standIn ? `${t('campaign.standIn')} ${id}` : (p.pawns[id]?.name ?? id);
};

/** The Mantel's slots in a row: tap one to read it, move it or sell it. */
export function MantelRow({
  run,
  onReorder,
  onSell,
}: {
  run: NightRun;
  onReorder?: (uids: string[]) => void;
  /** Selling is possible (at the Counter). */
  onSell?: (uid: string) => void;
}) {
  const [open, setOpen] = useState<ItemSpec | null>(null);
  const slots = mantelSlots(run);
  const used = mantelUsed(run);
  const index = open ? run.mantel.findIndex((i) => i.uid === open.uid) : -1;
  const move = (to: number) => {
    const uids = run.mantel.map((i) => i.uid);
    const [x] = uids.splice(index, 1);
    uids.splice(to, 0, x!);
    onReorder?.(uids);
    setOpen(null);
  };
  const actions: SheetAction[] = [];
  if (open && onReorder && index > 0)
    actions.push({ label: t('night.moveLeft'), onClick: () => move(index - 1) });
  if (open && onReorder && index >= 0 && index < run.mantel.length - 1)
    actions.push({ label: t('night.moveRight'), onClick: () => move(index + 1) });
  if (open && onSell && !isBolted(run, open.uid))
    actions.push({
      label: t('night.sell', {
        n: onLoan(run, open.uid) ? 0 : itemSellValue(open, 'superstition'),
      }),
      danger: true,
      onClick: () => {
        onSell(open.uid);
        setOpen(null);
      },
    });
  return (
    <div className="ln-mantel">
      <div className="ln-section-head">
        <h3 className="ln-eyebrow">{t('night.mantelTitle', { n: used, slots })}</h3>
        <span className="ln-hint">{t('night.mantelHint')}</span>
      </div>
      <Mantel
        items={run.mantel}
        slots={slots}
        objectOf={(item) => showMantelItem(run, item).object!}
        onOpen={setOpen}
        onReorder={onReorder}
      />
      {open && (
        <ItemSheet
          item={showMantelItem(run, open)}
          actions={actions}
          browse={
            index >= 0 && run.mantel.length > 1
              ? {
                  at: index,
                  of: run.mantel.length,
                  onStep: (d) =>
                    setOpen(run.mantel[(index + d + run.mantel.length) % run.mantel.length]!),
                }
              : undefined
          }
          note={
            onSell && isBolted(run, open.uid)
              ? t('night.boltedText')
              : index >= 0
                ? t('night.mantelPos', { n: index + 1 })
                : null
          }
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/** Everything the run owns, in one sheet. */
export function InventorySheet({
  profile,
  run,
  onReorder,
  onSell,
  onBench,
  onAbandon,
  onClose,
}: {
  profile: Profile;
  run: NightRun;
  onReorder?: (uids: string[]) => void;
  onSell?: (uid: string) => void;
  onBench?: (item: ItemSpec) => void;
  /** Abandoning the run: a quiet link at the very end. */
  onAbandon?: () => void;
  onClose: () => void;
}) {
  const [curio, setCurio] = useState<ItemSpec | null>(null);
  const [fixture, setFixture] = useState<string | null>(null);
  const curioActions: SheetAction[] = [];
  if (curio && onBench && isBenchCurio(curio.id))
    curioActions.push({
      label: t('night.use'),
      primary: true,
      onClick: () => {
        const c = curio;
        setCurio(null);
        onBench(c);
      },
    });
  if (curio && onSell)
    curioActions.push({
      label: t('night.sell', { n: itemSellValue(curio, 'curio') }),
      danger: true,
      onClick: () => {
        onSell(curio.uid);
        setCurio(null);
      },
    });
  return (
    <Modal title={t('night.inventory')} onClose={onClose} wide className="ln-inventory">
      <MantelRow run={run} onReorder={onReorder} onSell={onSell} />

      <div className="ln-section-head">
        <h3 className="ln-eyebrow">
          {t('night.curios', { n: run.curios.length, slots: curioSlots(run) })}
        </h3>
        <span className="ln-hint">{t('night.curiosHint')}</span>
      </div>
      {run.curios.length === 0 ? (
        <p className="ln-empty">{t('night.noneYet')}</p>
      ) : (
        <div className="ln-grid">
          {run.curios.map((c) => (
            <ItemCard key={c.uid} item={showCurio(c)} onClick={() => setCurio(c)} />
          ))}
        </div>
      )}

      <div className="ln-section-head">
        <h3 className="ln-eyebrow">{t('night.rate.title')}</h3>
        <span className="ln-hint">{t('night.rate.hint')}</span>
      </div>
      <RateCard run={run} />

      {run.fixtures.length > 0 && (
        <>
          <h3 className="ln-eyebrow">{t('night.fixtures')}</h3>
          <div className="ln-grid">
            {run.fixtures.map((f) => (
              <ItemCard key={f} item={showFixture(f)} onClick={() => setFixture(f)} />
            ))}
          </div>
        </>
      )}

      {run.favors.length > 0 && (
        <>
          <h3 className="ln-eyebrow">{t('night.favors')}</h3>
          <ul className="ln-list">
            {run.favors.map((f, i) => (
              <li key={`${f}-${i}`}>
                <strong>{itemName(`favor.${f}`)}</strong>
                <span className="ln-list-text">{itemText(`favor.${f}`)}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <h3 className="ln-eyebrow">{t('night.rosterTitle')}</h3>
      <ul className="ln-list">
        {run.roster.map((r) => (
          <li key={r.id}>
            <strong>{pawnLabel(profile, run, r.id)}</strong>
            {(run.carvings[r.id] ?? []).map((c) => (
              <span key={c.id} className="ln-list-text">
                {itemName(c.id)}: {itemText(c.id)}
              </span>
            ))}
          </li>
        ))}
      </ul>

      <p className="ln-seed">{t('night.seed', { seed: run.seed })}</p>
      {onAbandon && (
        <button type="button" className="ln-link-danger" onClick={onAbandon}>
          {t('campaign.abandonRun')}
        </button>
      )}

      {curio && (
        <ItemSheet item={showCurio(curio)} actions={curioActions} onClose={() => setCurio(null)} />
      )}
      {fixture && (
        <ItemSheet item={showFixture(fixture)} actions={[]} onClose={() => setFixture(null)} />
      )}
    </Modal>
  );
}
